import { useState, useEffect } from "react";
import axios from "axios";
import { X, IndianRupee, Building2, Target, Wallet, XCircle, Box, TrendingUp, Sparkles } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => { if (n == null) return '--'; const a = Math.abs(n); const s = n < 0 ? '-' : ''; if (a >= 1e7) return `${s}${(a/1e7).toFixed(2)} Cr`; if (a >= 1e5) return `${s}${(a/1e5).toFixed(2)} L`; if (a >= 1e3) return `${s}${(a/1e3).toFixed(1)}K`; return s + a.toLocaleString(); };
const fmtR = (n) => n ? '₹' + Math.round(n).toLocaleString('en-IN') : '--';
const cleanMd = (t) => t ? t.replace(/\*\*/g, '').replace(/\*/g, '').replace(/#{1,3}\s/g, '') : t;

export default function ProjectDetail({ isOpen, onClose, projectName, token, period, refDate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiInsight, setAiInsight] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !projectName) return;
    setLoading(true);
    setData(null);
    setAiInsight(null);

    (async () => {
      try {
        const params = { period };
        if (refDate) params.ref_date = refDate;
        // Period-filtered KPIs (sales, collection, cancellation)
        const kpiRes = await axios.get(`${API}/kpi/executive`, { headers: { Authorization: `Bearer ${token}` }, params: { ...params, project_name: projectName } });
        // Always-YTD for stock units + 13-month trend
        const ytdParams = { period: 'ytd' };
        if (refDate) ytdParams.ref_date = refDate;
        const stockRes = await axios.get(`${API}/kpi/executive`, { headers: { Authorization: `Bearer ${token}` }, params: { ...ytdParams, project_name: projectName } });

        const kpi = kpiRes.data;
        const stockData = stockRes.data;
        // Stock always from YTD/as-on-date
        const sold = stockData.sold_units || 0;
        const available = stockData.available_units || 0;
        const blocked = stockData.blocked_units || 0;
        const avgPriceSqft = kpi.sba > 0 ? Math.round(kpi.sales_value / kpi.sba) : 0;

        // Trend always from YTD (13 months) — merge sales + collection
        const salesTrend = stockData.sales_trend || [];
        const collTrend = stockData.collection_trend || [];
        const collMap = {};
        collTrend.forEach(t => { collMap[t.label] = t.value; });
        const trend = salesTrend.map(t => ({ ...t, collection: collMap[t.label] || 0 }));

        setData({ ...kpi, stock_total: stockData.total_stock_units || 0, sold, available, blocked, avgPriceSqft, trend });
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [isOpen, projectName, token, period, refDate]);

  const generateInsight = () => {
    if (aiLoading || !data) return;
    setAiLoading(true);
    const d = data;
    axios.post(`${API}/ai/copilot`, {
      message: `Detailed analysis for project "${projectName}": Sales ${fmt(d.sales_value)}, ${d.sales_count} units, SBA ${d.sba?.toLocaleString()} Sq.ft, Avg ₹${d.avgPriceSqft}/Sq.ft, Cancels ${fmt(d.cancel_value)} (${d.cancel_count} units), Collection ${fmt(d.collection)}, Stock ${d.stock_total} units (${d.sold} sold, ${d.available} available, ${d.blocked} blocked). Give: (1) Performance assessment in 2 lines, (2) Key risk, (3) One action with expected outcome. Use real estate context.`,
      session_id: `project-detail-${projectName}`,
      period, ref_date: refDate,
    }, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setAiInsight(cleanMd(r.data.response)))
      .catch(() => setAiInsight("Unable to generate."))
      .finally(() => setAiLoading(false));
  };

  if (!isOpen) return null;
  const d = data || {};

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4" data-testid="project-detail-modal">
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-2xl max-h-[92vh] sm:max-h-[85vh] overflow-y-auto z-10 pb-safe">
        <div className="sticky top-0 bg-[#0F1D32] text-white px-4 py-3 rounded-t-2xl flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <Building2 className="h-5 w-5 text-blue-400" />
            <div>
              <h2 className="text-base font-bold" style={{ fontFamily: 'Manrope' }}>{projectName}</h2>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">Project Deep-Dive</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-lg"><X className="h-5 w-5 text-slate-400" /></button>
        </div>

        {loading ? (
          <div className="p-10 text-center"><div className="inline-block h-6 w-6 border-2 border-slate-200 border-t-blue-600 rounded-full animate-spin" /></div>
        ) : data ? (
          <div className="p-4 sm:p-5 space-y-4">
            {/* KPI Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              <div className="bg-blue-50 rounded-xl p-3"><p className="text-[9px] font-bold text-blue-500 uppercase">Sales Value</p><p className="text-base font-bold text-blue-900 mt-0.5" style={{ fontFamily: 'Manrope' }}>{fmt(d.sales_value)}</p><p className="text-[10px] text-blue-600">{d.sales_count || 0} units</p></div>
              <div className="bg-emerald-50 rounded-xl p-3"><p className="text-[9px] font-bold text-emerald-500 uppercase">Collection</p><p className="text-base font-bold text-emerald-900 mt-0.5" style={{ fontFamily: 'Manrope' }}>{fmt(d.collection)}</p><p className="text-[10px] text-emerald-600">{d.collection_efficiency ? `${d.collection_efficiency}% of target` : ''}</p></div>
              <div className="bg-red-50 rounded-xl p-3"><p className="text-[9px] font-bold text-red-500 uppercase">Cancellations</p><p className="text-base font-bold text-red-900 mt-0.5" style={{ fontFamily: 'Manrope' }}>{fmt(d.cancel_value)}</p><p className="text-[10px] text-red-600">{d.cancel_count || 0} units · {d.sales_value > 0 ? `${(d.cancel_value / d.sales_value * 100).toFixed(1)}% rate` : ''}</p></div>
              <div className="bg-indigo-50 rounded-xl p-3"><p className="text-[9px] font-bold text-indigo-500 uppercase">Avg ₹/Sq.ft</p><p className="text-base font-bold text-indigo-900 mt-0.5" style={{ fontFamily: 'Manrope' }}>{d.avgPriceSqft > 0 ? fmtR(d.avgPriceSqft) : '--'}</p><p className="text-[10px] text-indigo-600">{d.sba > 0 ? `${Math.round(d.sba).toLocaleString('en-IN')} Sq.ft` : ''}</p></div>
            </div>

            {/* 1. Absorption + 2. Sales Target + 3. Collection Target */}
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-xl border border-slate-200 p-2.5 text-center">
                <p className="text-[8px] font-bold text-slate-400 uppercase">Absorption</p>
                <p className={`text-lg font-bold mt-0.5 ${d.stock_total > 0 && (d.sold / d.stock_total * 100) >= 50 ? 'text-emerald-600' : 'text-amber-600'}`} style={{ fontFamily: 'Manrope' }}>{d.stock_total > 0 ? `${(d.sold / d.stock_total * 100).toFixed(0)}%` : '--'}</p>
                <p className="text-[8px] text-slate-400">{d.sold}/{d.stock_total} units</p>
              </div>
              <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-2.5 text-center">
                <p className="text-[8px] font-bold text-blue-500 uppercase">Sales Target</p>
                <p className="text-sm font-bold text-blue-900 mt-0.5" style={{ fontFamily: 'Manrope' }}>{d.target_value > 0 ? fmt(d.target_value) : '--'}</p>
                <p className={`text-[8px] font-medium ${d.achievement_pct >= 80 ? 'text-emerald-600' : 'text-amber-600'}`}>Ach: {d.achievement_pct || 0}%</p>
              </div>
              <div className="rounded-xl border border-teal-200 bg-teal-50/50 p-2.5 text-center">
                <p className="text-[8px] font-bold text-teal-500 uppercase">Coll Target</p>
                <p className="text-sm font-bold text-teal-900 mt-0.5" style={{ fontFamily: 'Manrope' }}>{d.collection_target > 0 ? fmt(d.collection_target) : '--'}</p>
                <p className={`text-[8px] font-medium ${d.collection_efficiency >= 80 ? 'text-emerald-600' : 'text-amber-600'}`}>Eff: {d.collection_efficiency || 0}%</p>
              </div>
            </div>

            {/* Stock + Secondary */}
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              <div className="bg-slate-50 rounded-lg p-2.5 text-center"><p className="text-xs font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{fmt(d.net_sales)}</p><p className="text-[8px] text-slate-500">Net Sales</p></div>
              <div className="bg-slate-50 rounded-lg p-2.5 text-center"><p className="text-xs font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{d.net_sba ? `${Math.round(d.net_sba).toLocaleString('en-IN')}` : '0'}</p><p className="text-[8px] text-slate-500">Net SBA</p></div>
              <div className="bg-slate-50 rounded-lg p-2.5 text-center"><p className="text-xs font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{d.stock_total || 0}</p><p className="text-[8px] text-slate-500">Total Units</p></div>
              <div className="bg-emerald-50 rounded-lg p-2.5 text-center"><p className="text-xs font-bold text-emerald-700">{d.sold || 0}</p><p className="text-[8px] text-emerald-600">Sold</p></div>
              <div className="bg-blue-50 rounded-lg p-2.5 text-center"><p className="text-xs font-bold text-blue-700">{d.available || 0}</p><p className="text-[8px] text-blue-600">Available</p></div>
              <div className="bg-amber-50 rounded-lg p-2.5 text-center"><p className="text-xs font-bold text-amber-700">{d.blocked || 0}</p><p className="text-[8px] text-amber-600">Blocked</p></div>
            </div>

            {/* 5. Sales + Collection Trend */}
            {d.trend?.length > 1 && (
              <div className="space-y-4">
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Sales Trend (13 Months)</p>
                  <ResponsiveContainer width="100%" height={150}>
                    <BarChart data={d.trend} barCategoryGap="20%">
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                      <XAxis dataKey="label" tick={{ fontSize: 8, fill: '#94A3B8' }} />
                      <YAxis tick={{ fontSize: 8, fill: '#94A3B8' }} tickFormatter={fmt} width={45} />
                      <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0', fontSize: '10px' }} formatter={v => [fmt(v), 'Sales']} />
                      <Bar dataKey="value" name="Sales" radius={[4, 4, 0, 0]} barSize={8}>
                        {d.trend.map((e, i) => <Cell key={i} fill={e.in_period ? '#1D4ED8' : '#CBD5E1'} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                {d.trend.some(t => t.collection > 0) && (
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Collection Trend (13 Months)</p>
                  <ResponsiveContainer width="100%" height={150}>
                    <BarChart data={d.trend} barCategoryGap="20%">
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                      <XAxis dataKey="label" tick={{ fontSize: 8, fill: '#94A3B8' }} />
                      <YAxis tick={{ fontSize: 8, fill: '#94A3B8' }} tickFormatter={fmt} width={45} />
                      <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0', fontSize: '10px' }} formatter={v => [fmt(v), 'Collection']} />
                      <Bar dataKey="collection" name="Collection" radius={[4, 4, 0, 0]} barSize={8} fill="#10B981" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                )}
              </div>
            )}

            {/* AI Insight */}
            <div className="rounded-xl bg-gradient-to-r from-blue-50 to-slate-50 border border-blue-100 p-3.5">
              {aiInsight ? (
                <div><p className="text-[10px] font-bold text-blue-600 uppercase mb-1">AI Analysis</p><p className="text-xs text-slate-700 leading-relaxed">{aiInsight}</p></div>
              ) : (
                <button onClick={generateInsight} disabled={aiLoading} className="w-full flex items-center justify-center gap-2 py-2 text-xs font-semibold text-blue-700 hover:text-blue-900 transition-colors">
                  {aiLoading ? <><div className="h-3 w-3 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" /> Analyzing...</> : <><Sparkles className="h-3.5 w-3.5" /> Generate AI Analysis for {projectName}</>}
                </button>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
